// A line's pick tickets and POs as text. The grid shows them in two columns, never merged (Isaac,
// V7): SO / PT # "9000101-9000103, 9000110" and PO "716549612, 716549613". Consecutive pick tickets
// collapse into a range; a Deposco SO # (SO230792) is listed as it is. `poPtText` is a line's name
// in a dialog or a notice.

import type { LineView, PtChange } from "./assemble";
import { formatDay } from "./days";

const isNumber = (p: string) => /^\d+$/.test(p);

function rangeParts(pickTickets: readonly string[]): string[] {
  const all = [...new Set(pickTickets)];
  const nums = all
    .filter(isNumber)
    .map(Number)
    .sort((a, b) => a - b);
  // SO #s and anything else that is not a number: after the ranges, in order.
  const parts: string[] = [];
  for (let i = 0; i < nums.length;) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j]! + 1) j++;
    parts.push(j > i ? `${nums[i]}-${nums[j]}` : String(nums[i]));
    i = j + 1;
  }
  return [...parts, ...all.filter((p) => !isNumber(p)).sort()];
}

export const ptRanges = (pickTickets: readonly string[]): string => rangeParts(pickTickets).join(", ");

/** "a, b, c +12 more" — every part when `max` is not given. */
const firstOf = (parts: readonly string[], max?: number) =>
  max !== undefined && parts.length > max
    ? `${parts.slice(0, max).join(", ")} +${parts.length - max} more`
    : parts.join(", ");

/** The pick tickets still open, else all of them (a line gone from A2000), as ranges. */
/** What the pick-ticket texts read of a line: its pick tickets' numbers, and which are gone. */
type PtsOf = { pts: readonly Pick<LineView["pts"][number], "pickTicket" | "gone">[] };

const ptParts = (line: PtsOf) => {
  const live = rangeParts(line.pts.filter((p) => !p.gone).map((p) => p.pickTicket));
  return live.length ? live : rangeParts(line.pts.map((p) => p.pickTicket));
};

/**
 * The SO / PT # cell. With `max`, at most that many ranges, then "+N more" (a 531-pick-ticket line);
 * the full text goes in the tooltip.
 */
export const ptText = (line: PtsOf, max?: number): string => firstOf(ptParts(line), max);

/** The PO cell: the customer POs (A2000 ORDER_NO), "+N more" past `max`. */
export const poText = (line: Pick<LineView, "pos">, max?: number): string => firstOf(line.pos, max);

/** How a line names its pick tickets: "PT", or "SO" on a Deposco line. */
export const ptWord = (line: Partial<Pick<LineView, "source">>) => (line.source === "Deposco" ? "SO" : "PT");

const PT_DATE_WORDS: Record<PtChange["field"], string> = { start: "First ship", cancel: "Cancel", ptDate: "Received" };
/** "PT 1810297: First ship 10/02 → 10/06" (a Deposco SO # is its own name: "SO230792: …"). */
export const ptChangeText = (x: PtChange): string =>
  `${isNumber(x.pickTicket) ? "PT " : ""}${x.pickTicket}: ${PT_DATE_WORDS[x.field]} ${x.from ? formatDay(x.from) : "blank"} → ${x.to ? formatDay(x.to) : "blank"}`;

/** A line's name in a dialog or a notice: "2 P.O · PT 9000101-9000103 / 716549612, 716549613". */
export function poPtText(
  line: Pick<LineView, "pts" | "pos"> & Partial<Pick<LineView, "source">>,
  max?: number,
): string {
  const pts = firstOf(ptParts(line), max);
  if (!line.pos.length) return `${ptWord(line)} ${pts}`;
  const head = `${line.pos.length} P.O${line.pos.length > 1 ? "s" : ""}`;
  return `${head} · ${ptWord(line)} ${pts} / ${firstOf(line.pos, max)}`;
}

/**
 * How many pick tickets the SO / PT # cell names: the open ones, else all of them — each pick
 * ticket of a range counts ("1810289-1810290" is 2), not the comma-separated pieces.
 */
export function ptCount(line: PtsOf): number {
  const live = new Set(line.pts.filter((p) => !p.gone).map((p) => p.pickTicket));
  return live.size || new Set(line.pts.map((p) => p.pickTicket)).size;
}

/** How many distinct POs the PO cell names. */
export const poCount = (line: Pick<LineView, "pos">): number => new Set(line.pos.filter(Boolean)).size;

/**
 * "(2) " before a cell of several: shown only — sorting, search, filters, Ask AI and exports read
 * the values without it. One value (or none): nothing.
 */
export const countPrefix = (n: number): string => (n > 1 ? `(${n}) ` : "");
